import { X } from 'lucide-react-native';
import { type KeyboardEvent, type ReactNode, useCallback, useEffect, useRef } from 'react';
import {
  Animated,
  Dimensions,
  Easing,
  Modal,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  View,
} from 'react-native';
import { useReducedMotion } from '../../hooks/useReducedMotion';
import { colors } from '../../theme/colors';
import { motion } from '../../theme/motion';
import { tokens } from '../../theme/tokens';
import { Text } from './Text';

type SheetSize = 'sm' | 'md' | 'lg' | 'full';
type SheetPosition = 'bottom' | 'center' | 'right';

/** How far the panel travels before settling. */
const PANEL_RISE = 12;

/**
 * The panel rises into place on the entrance ease while the Modal fades the
 * scrim behind it, and leaves faster than it arrives. Reduced motion skips
 * straight to the settled state rather than animating at all.
 */
function useSheetPanelEntrance(visible: boolean) {
  const reducedMotion = useReducedMotion();
  const progress = useRef(new Animated.Value(visible ? 1 : 0)).current;

  useEffect(() => {
    if (reducedMotion) {
      progress.setValue(visible ? 1 : 0);
      return;
    }
    Animated.timing(progress, {
      toValue: visible ? 1 : 0,
      duration: visible ? motion.durations.normal : motion.durations.fast,
      easing: visible ? Easing.bezier(0.16, 1, 0.3, 1) : Easing.bezier(0.4, 0, 1, 1),
      useNativeDriver: true,
    }).start();
  }, [visible, reducedMotion, progress]);

  return {
    opacity: progress,
    transform: [
      {
        translateY: progress.interpolate({
          inputRange: [0, 1],
          outputRange: [PANEL_RISE, 0],
        }),
      },
    ],
  };
}

interface SheetProps {
  visible: boolean;
  onClose: () => void;
  title?: string;
  children: ReactNode;
  size?: SheetSize;
  position?: SheetPosition;
  hideClose?: boolean;
  bodyScrollable?: boolean;
  disableBodyPadding?: boolean;
}

const SIZE_MAP: Record<SheetSize, number> = {
  sm: 400,
  md: 560,
  lg: 720,
  full: Dimensions.get('window').width,
};

const CLOSE_ICON_SIZE = 20;
const CLOSE_HIT_SLOP = { top: 8, right: 8, bottom: 8, left: 8 };

export function Sheet({
  visible,
  onClose,
  title,
  children,
  size = 'md',
  position = 'bottom',
  hideClose = false,
  bodyScrollable = true,
  disableBodyPadding = false,
}: Readonly<SheetProps>) {
  const contentRef = useRef<View>(null);
  const panelEntrance = useSheetPanelEntrance(visible);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      if (e.key !== 'Tab' || Platform.OS !== 'web') return;

      const container = contentRef.current as unknown as HTMLElement | null;
      if (!container) return;

      const focusable = container.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (!visible || Platform.OS !== 'web') return;
    const container = contentRef.current as unknown as HTMLElement | null;
    if (!container) return;
    const firstFocusable = container.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (firstFocusable) {
      requestAnimationFrame(() => firstFocusable.focus());
    }
  }, [visible]);

  const windowWidth = Dimensions.get('window').width;
  const windowHeight = Dimensions.get('window').height;
  const resolvedWidth = size === 'full' ? windowWidth : Math.min(SIZE_MAP[size], windowWidth);

  const contentContainerStyle = (() => {
    switch (position) {
      case 'bottom':
        return [styles.contentBase, styles.contentBottom, { maxHeight: windowHeight * 0.9 }];
      case 'center':
        return [
          styles.contentBase,
          styles.contentCenter,
          { maxWidth: resolvedWidth, width: '100%' as const },
        ];
      case 'right':
        return [
          styles.contentBase,
          styles.contentRight,
          { width: resolvedWidth, maxWidth: windowWidth },
        ];
      default:
        return [styles.contentBase];
    }
  })();

  const backdropStyle = (() => {
    switch (position) {
      case 'bottom':
        return [styles.backdrop, styles.backdropBottom];
      case 'center':
        return [styles.backdrop, styles.backdropCenter];
      case 'right':
        return [styles.backdrop, styles.backdropRight];
      default:
        return [styles.backdrop];
    }
  })();

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
      statusBarTranslucent
    >
      <View style={backdropStyle}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel="Close sheet"
        />
        {/* The Modal fades the scrim; the panel rises into place behind it so
            the sheet arrives rather than appearing. */}
        <Animated.View
          ref={contentRef}
          style={[contentContainerStyle, panelEntrance]}
          accessibilityRole="none"
          {...(Platform.OS === 'web' ? { onKeyDown: handleKeyDown } : {})}
        >
          {title ? (
            <View style={styles.header}>
              <Text variant="h6" style={styles.headerTitle} numberOfLines={1}>
                {title}
              </Text>
              {!hideClose && (
                <Pressable
                  onPress={onClose}
                  hitSlop={CLOSE_HIT_SLOP}
                  accessibilityRole="button"
                  accessibilityLabel="Close"
                  style={styles.closeButton}
                >
                  <X size={CLOSE_ICON_SIZE} color={colors.text.tertiary} />
                </Pressable>
              )}
            </View>
          ) : !hideClose ? (
            <View style={styles.headerNoTitle}>
              <Pressable
                onPress={onClose}
                hitSlop={CLOSE_HIT_SLOP}
                accessibilityRole="button"
                accessibilityLabel="Close"
                style={styles.closeButton}
              >
                <X size={CLOSE_ICON_SIZE} color={colors.text.tertiary} />
              </Pressable>
            </View>
          ) : null}
          {bodyScrollable ? (
            <ScrollView
              style={styles.body}
              contentContainerStyle={disableBodyPadding ? undefined : styles.bodyContent}
              showsVerticalScrollIndicator={false}
              bounces={false}
            >
              {children}
            </ScrollView>
          ) : (
            <View style={[styles.body, !disableBodyPadding && styles.bodyContent]}>{children}</View>
          )}
        </Animated.View>
      </View>
    </Modal>
  );
}

Sheet.displayName = 'Sheet';

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: colors.background.overlay,
  },
  backdropBottom: {
    justifyContent: 'flex-end',
  },
  backdropCenter: {
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.xl,
  },
  backdropRight: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },

  contentBase: {
    backgroundColor: colors.background.card,
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -4 },
        shadowOpacity: 0.25,
        shadowRadius: 16,
      },
      android: {
        elevation: 24,
      },
      web: {
        boxShadow: '0 -4px 32px rgba(0, 0, 0, 0.3)',
      } as any,
    }),
  },
  contentBottom: {
    borderTopLeftRadius: tokens.radius['2xl'],
    borderTopRightRadius: tokens.radius['2xl'],
    maxHeight: '90%',
  },
  contentCenter: {
    borderRadius: tokens.radius.xl,
    maxHeight: '80%',
  },
  contentRight: {
    height: '100%',
    borderTopLeftRadius: tokens.radius.xl,
    borderBottomLeftRadius: tokens.radius.xl,
  },

  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: tokens.spacing.xl,
    paddingVertical: tokens.spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border.muted,
  },
  headerTitle: {
    flex: 1,
    marginRight: tokens.spacing.md,
  },
  headerNoTitle: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    paddingHorizontal: tokens.spacing.xl,
    paddingTop: tokens.spacing.lg,
  },
  closeButton: {
    padding: tokens.spacing.xs,
    borderRadius: tokens.radius.sm,
  },

  body: {
    flexShrink: 1,
  },
  bodyContent: {
    padding: tokens.spacing.xl,
  },
});
